import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { installAnalytics, trackPageview, resolveVisitorAsAnonymous } from './analytics.js';
import { startKit, resetForNavigation } from './kitAdapter.js';

// Mounted once, in root.jsx's App. Owns pageviews for the whole app.
//
// The tracker's own auto-pageview is turned OFF (data-auto-pageview="false",
// set by services/analytics.js's loader). It works perfectly well with React
// Router's pushState navigation - that is not why it is off. It is off because
// it would send window.location verbatim: /app/ro/1000123 rather than
// /app/ro/:id, and /sign-in?user_hint=<email> rather than /sign-in. Owning
// pageviews here is what makes urls.js's normalization unconditional.
export default function usePageview() {
  const location = useLocation();
  const previous = useRef(null);

  useEffect(() => {
    installAnalytics();
    startKit();
  }, []);

  useEffect(() => {
    // React 18+ StrictMode double-invokes effects in dev, and a route change
    // that only changes the query string or hash is not a new page.
    if (previous.current === location.pathname) return;
    previous.current = location.pathname;

    // Outside /app there is no signed-in product surface and therefore no
    // admin to suppress, so the beforeSend gate is opened here rather than
    // leaving every landing-page and tour event queued behind a POST
    // /auth/verify that is never made. Inside /app the gate stays closed until
    // routes/protected.jsx settles it - that call is already in flight, and a
    // pageview raised before it lands is held, not guessed at.
    if (!location.pathname.startsWith('/app')) resolveVisitorAsAnonymous();

    // Order matters: the kit's per-page counters are reset FIRST, so the
    // time-on-page window starts at the pageview rather than a beat after it.
    resetForNavigation();
    trackPageview(location.pathname);
  }, [location.pathname]);
}
